import CloseIcon from '@mui/icons-material/Close';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import React, { FC } from 'react';
import { IOptionsProps } from '../Form/Select/select.component';

interface IListBrandFilterProps {
  label?: string;
  value: string;
  options: IOptionsProps[];
  onChange: (brand: string) => void;
  placeholder?: string;
  sx?: SxProps<Theme>;
}

const ListBrandFilter: FC<IListBrandFilterProps> = ({
  label = 'Marca',
  value,
  options,
  onChange,
  placeholder = 'Todas as marcas',
  sx = {},
}) => {
  const hasFilter = Boolean(value);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        width: { xs: '100%', sm: 'auto' },
        minWidth: { sm: 260 },
        maxWidth: { md: 340 },
        height: 40,
        px: 1.25,
        border: '1px solid',
        borderColor: hasFilter ? 'primary.main' : 'divider',
        borderRadius: 1,
        bgcolor: hasFilter ? 'action.selected' : 'background.paper',
        boxShadow: hasFilter ? '0 0 0 1px rgba(25, 118, 210, 0.08)' : 'none',
        transition: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
        ...sx,
      }}
    >
      <FilterAltOutlinedIcon
        sx={{
          fontSize: 18,
          color: hasFilter ? 'primary.main' : 'text.secondary',
          flexShrink: 0,
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Typography
          variant="caption"
          color={hasFilter ? 'primary.main' : 'text.secondary'}
          sx={{
            display: 'block',
            lineHeight: 1.1,
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {label}
        </Typography>

        <Select
          variant="standard"
          disableUnderline
          displayEmpty
          value={value}
          onChange={(event) => onChange(String(event.target.value))}
          renderValue={(selected) => (
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: selected ? 600 : 400,
                color: selected ? 'text.primary' : 'text.secondary',
                lineHeight: 1.3,
              }}
            >
              {selected || placeholder}
            </Typography>
          )}
          sx={{
            width: '100%',
            height: 22,
            '& .MuiSelect-select': {
              p: 0,
              pr: '24px !important',
              minHeight: 'unset !important',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiSelect-icon': {
              right: 0,
              color: 'text.secondary',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 320,
                mt: 0.5,
                minWidth: 240,
              },
            },
          }}
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.secondary">
              {placeholder}
            </Typography>
          </MenuItem>
          {options.map((brand) => (
            <MenuItem key={brand.value} value={brand.label}>
              <Typography variant="body2" noWrap>
                {brand.label}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </Box>

      {hasFilter ? (
        <IconButton
          size="small"
          aria-label="Limpar filtro de marca"
          onClick={() => onChange('')}
          sx={{
            flexShrink: 0,
            color: 'text.secondary',
            '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ) : null}
    </Box>
  );
};

export { ListBrandFilter };
